/**
 * Chat Service
 *
 * Handles Slack-style messaging: conversations (channels, DMs, groups),
 * messages with threading, read tracking, and bitacora integration.
 *
 * @module services/chatService
 */

import { supabase } from './supabase';
import { terminoParaFiltroSinNormalizar } from '../utils/normalizacionBusqueda';
import { compressImage, generateUniqueFilename } from '../utils/fileUpload';

// ============================================================
// FILE UPLOADS
// ============================================================

/** Bucket de adjuntos del chat. PRIVADO — ver el bloque de abajo. */
export const CHAT_ATTACHMENTS_BUCKET = 'chat-attachments';

/** 1 hora, igual que el resto del repo (ver "File Storage" en CLAUDE.md). */
export const CHAT_ATTACHMENT_SIGN_TTL_SECONDS = 3600;

/**
 * Extrae el path interno del objeto a partir de una URL de Supabase Storage.
 * Acepta las tres formas (`sign` / `public` / `authenticated`); un valor que
 * no es http(s) se asume que ya es un path pelado.
 *
 * Mismo helper que `progressService` / `purchaseService` — no se puede
 * importar de alla sin arrastrar el bucket ajeno, asi que se repite la
 * aritmetica y nada mas.
 * @param {string} url
 * @returns {string|null}
 */
const extractStoragePath = (url) => {
  if (!url || typeof url !== 'string') return null;
  if (!/^https?:\/\//i.test(url)) return url; // ya es un path
  const match = url.match(
    new RegExp(`/storage/v1/object/(?:sign|public|authenticated)/${CHAT_ATTACHMENTS_BUCKET}/([^?]+)`)
  );
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

/**
 * Path estable de un adjunto, o null si no es un objeto de este bucket
 * (por ejemplo una URL externa que el agente paso tal cual).
 * @param {string|{path?: string, url?: string}} attachment
 * @returns {string|null}
 */
export const chatAttachmentPath = (attachment) => {
  if (!attachment) return null;
  if (typeof attachment === 'string') return extractStoragePath(attachment);
  return attachment.path || extractStoragePath(attachment.url);
};

/**
 * Normaliza los adjuntos JSONB de una fila de `chat_messages`.
 *
 * Aguanta las dos formas: el array nativo (lo correcto desde #453) y el
 * STRING doble-codificado de las filas historicas — las TRES filas con
 * adjunto que hay en produccion hoy son de la forma vieja, medido el
 * 2026-07-31.
 * @param {*} raw
 * @returns {Array}
 */
const parseAttachments = (raw) => {
  const parsed = typeof raw === 'string'
    ? (() => {
        try {
          return JSON.parse(raw || '[]');
        } catch {
          return [];
        }
      })()
    : (raw || []);
  return Array.isArray(parsed) ? parsed : [];
};

/**
 * Firma una URL viva para un adjunto del chat.
 *
 * Best-effort a proposito (mismo criterio que `progressService.resolvePhotoUrl`
 * y `purchaseService.resolveAttachmentUrl`): si no se puede firmar, se
 * devuelve el valor guardado en vez de romper la conversacion entera.
 * @param {string|{path?: string, url?: string}} attachment
 * @returns {Promise<string|null>}
 */
export const resolveChatAttachmentUrl = async (attachment) => {
  const stored = typeof attachment === 'string' ? attachment : attachment?.url || null;
  const path = chatAttachmentPath(attachment);
  if (!path) return stored;

  const { data, error } = await supabase.storage
    .from(CHAT_ATTACHMENTS_BUCKET)
    .createSignedUrl(path, CHAT_ATTACHMENT_SIGN_TTL_SECONDS);

  if (error || !data?.signedUrl) {
    console.warn('[CHAT] No se pudo firmar el adjunto, se devuelve el valor guardado:', error);
    return stored;
  }
  return data.signedUrl;
};

/**
 * Devuelve la fila de mensaje con cada adjunto re-firmado.
 *
 * MessageBubble pinta `attachment.url`, asi que la resolucion vive aca (en el
 * servicio) y el componente no cambia — mismo reparto que en #332, donde
 * `resolvePhotoUrls` resuelve en `progressService` y la UI ni se entera.
 * @param {Object} message - fila de chat_messages
 * @returns {Promise<Object>}
 */
export const resolveMessageAttachments = async (message) => {
  if (!message) return message;
  const attachments = parseAttachments(message.attachments);
  if (attachments.length === 0) return message;

  const resolved = await Promise.all(
    attachments.map(async (att) => {
      const url = await resolveChatAttachmentUrl(att);
      if (typeof att === 'string') return { url };
      return { ...att, url };
    })
  );

  return { ...message, attachments: resolved };
};

/**
 * Ver {@link resolveMessageAttachments}.
 * @param {Array<Object>} messages
 * @returns {Promise<Array<Object>>}
 */
export const resolveMessagesAttachments = async (messages) => {
  if (!messages?.length) return messages;
  return Promise.all(messages.map(resolveMessageAttachments));
};

/**
 * Forma que se PERSISTE en `chat_messages.attachments`: el path estable, no
 * la URL.
 *
 * Una URL firmada persistida muere en una hora (#332) y una URL publica
 * persistida es el agujero que cierra esta tanda: el bucket era `public=true`
 * y cualquiera con el link bajaba la foto sin login. Los adjuntos que no son
 * de este bucket (URL externa) se dejan como estaban.
 * @param {string|{url?: string}} attachment
 * @returns {Object}
 */
const toPersistedAttachment = (attachment) => {
  const path = chatAttachmentPath(attachment);
  if (!path) {
    return typeof attachment === 'string' ? { url: attachment } : attachment;
  }
  if (typeof attachment === 'string') return { path };
  const { url, ...rest } = attachment;
  return { ...rest, path };
};

/**
 * Upload a chat attachment (image OR document) to Supabase Storage.
 *
 * #451: documents are allowed too. Only images are compressed — running a
 * canvas-based compressor over a PDF would corrupt it. The bucket's
 * `allowed_mime_types` is the real gate (see
 * `supabase/migrations/20260727150000_chat_attachments_documents.sql`);
 * Storage rejects a disallowed type before RLS is even consulted.
 *
 * FUGA CERRADA — `chat-attachments` era el unico bucket de evidencia PUBLICO
 * del sistema. Medido contra prod el 2026-07-31 con curl sin apikey ni
 * Authorization: dos fotos de chat bajadas con HTTP 200, sin firma y sin
 * expiracion, mientras `progress-photos`, `receipts` y `quotation-files`
 * contestaban NoSuchBucket. Esta funcion persistia ese `getPublicUrl` dentro
 * de `chat_messages.attachments`, o sea que el link quedaba grabado para
 * siempre y viajaba en notificaciones, deep links y en el JSON de las tools.
 *
 * Ahora devuelve el PATH (que es lo que se guarda) y una URL FIRMADA a 1 hora
 * solo para el preview inmediato del que sube.
 */
export const uploadChatAttachment = async (file, conversationId) => {
  try {
    // Compress large images (never documents)
    let fileToUpload = file;
    if (file.type.startsWith('image/') && file.size > 2 * 1024 * 1024) {
      fileToUpload = await compressImage(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
      });
    }

    const filename = generateUniqueFilename(file.name, 'chat');
    const path = `${conversationId}/${filename}`;

    const { data, error } = await supabase.storage
      .from(CHAT_ATTACHMENTS_BUCKET)
      .upload(path, fileToUpload, {
        cacheControl: '3600',
        upsert: false,
        // Be explicit: `generateUniqueFilename` can rewrite the extension, and
        // Storage would otherwise sniff the type from the path and fall back to
        // application/octet-stream, which the bucket's allow-list rejects.
        contentType: file.type || 'application/octet-stream',
      });

    if (error) throw error;

    // Solo para el preview optimista de quien acaba de subir. Lo que se
    // guarda en la base es `path` (ver toPersistedAttachment).
    const previewUrl = await resolveChatAttachmentUrl({ path: data.path });

    return {
      url: previewUrl || '',
      path: data.path,
      type: file.type,
      name: file.name,
      size: file.size,
      error: null,
    };
  } catch (error) {
    console.error('[CHAT] Upload attachment error:', error);
    return { url: '', path: '', error };
  }
};

// ============================================================
// CONVERSATIONS
// ============================================================

/**
 * Get user's conversations with last message and unread count
 *
 * Perf (#452): this used to run 3-4 extra queries PER conversation (last
 * message, participant count, DM counterpart, unread count) inside a
 * `Promise.all` — i.e. ~1 + 4N round-trips to Supabase for N conversations.
 * It now runs a FIXED number of queries (participations + 3 batched
 * `.in()`/RPC lookups) regardless of how many conversations the user has:
 *   1. participations (+ joined conversation) for the current user
 *   2. all non-deleted messages across those conversations, reduced
 *      client-side to the latest one per conversation_id
 *   3. all participant rows across those conversations, reduced
 *      client-side to a count + DM counterpart per conversation_id
 *   4. get_user_unread_counts RPC (already a single batched call — was
 *      being reimplemented per-conversation as a redundant gt()/neq() count)
 * See src/services/__tests__/chatService.getConversations.perf.test.js for
 * the regression test that asserts query count stays flat as N grows.
 */
export const getConversations = async (options = {}) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { type = null } = options;

    // Get conversations where user is a participant
    const { data: participations, error } = await supabase
      .from('chat_participants')
      .select(`
        conversation_id,
        role,
        last_read_at,
        is_muted,
        conversation:chat_conversations!conversation_id(
          id, type, project_id, name, description, created_by,
          is_archived, last_message_at, created_at,
          project:projects(id, title, project_number)
        )
      `)
      .eq('user_id', user.id);

    if (error) throw error;

    if (!participations || participations.length === 0) {
      return { data: [], error: null };
    }

    const myParticipations = participations
      .filter((p) => p.conversation)
      .filter((p) => !type || p.conversation.type === type);

    if (myParticipations.length === 0) {
      return { data: [], error: null };
    }

    const convIds = myParticipations.map((p) => p.conversation.id);

    // --- Batch: last non-deleted message per conversation, in ONE query ---
    const { data: lastMessageCandidates } = await supabase
      .from('chat_messages')
      .select(`
        id, conversation_id, content, message_type, created_at,
        sender:profiles!sender_id(id, full_name, avatar_url)
      `)
      .in('conversation_id', convIds)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false });

    const lastMessageByConv = new Map();
    for (const msg of lastMessageCandidates || []) {
      // Rows arrive newest-first, so the first one seen per conversation_id
      // is the latest — no need for a second pass or per-row query.
      if (!lastMessageByConv.has(msg.conversation_id)) {
        lastMessageByConv.set(msg.conversation_id, msg);
      }
    }

    // --- Batch: every participant row across all conversations, ONE query -
    // Replaces the old per-conversation participant-count query AND the
    // per-conversation DM-counterpart query.
    const { data: allParticipants } = await supabase
      .from('chat_participants')
      .select(`
        conversation_id, user_id,
        user:profiles!user_id(id, full_name, avatar_url, role)
      `)
      .in('conversation_id', convIds);

    const participantsByConv = new Map();
    for (const row of allParticipants || []) {
      const list = participantsByConv.get(row.conversation_id) || [];
      list.push(row);
      participantsByConv.set(row.conversation_id, list);
    }

    // --- Batch: unread counts for ALL conversations via the existing RPC ---
    // (this RPC already exists and is already O(1) — the old code was
    // needlessly re-deriving the same number per conversation with its own
    // gt()/neq() count query instead of just calling this once.)
    const { data: unreadRows } = await supabase.rpc('get_user_unread_counts', {
      p_user_id: user.id,
    });
    const unreadByConv = new Map(
      (unreadRows || []).map((row) => [row.conversation_id, row.unread_count])
    );

    // --- Assemble ------------------------------------------------------
    const conversations = myParticipations.map((p) => {
      const conv = p.conversation;
      const convParticipants = participantsByConv.get(conv.id) || [];

      let dmParticipant = null;
      if (conv.type === 'direct') {
        const other = convParticipants.find((row) => row.user_id !== user.id);
        dmParticipant = other?.user || null;
      }

      return {
        ...conv,
        lastMessage: lastMessageByConv.get(conv.id) || null,
        participantCount: convParticipants.length,
        dmParticipant,
        unreadCount: unreadByConv.get(conv.id) || 0,
        isMuted: p.is_muted,
        myRole: p.role,
        lastReadAt: p.last_read_at,
      };
    });

    // Sort by last_message_at descending
    conversations.sort((a, b) => {
      const timeA = new Date(a.last_message_at || a.created_at || 0);
      const timeB = new Date(b.last_message_at || b.created_at || 0);
      return timeB - timeA;
    });

    return { data: conversations, error: null };
  } catch (error) {
    console.error('[CHAT] Get conversations error:', error);
    return { data: null, error };
  }
};

/**
 * Create a new conversation (channel, group, or DM)
 */
export const createConversation = async ({ type, name, description, project_id, participantIds = [] }) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // For DMs, use the dedicated function
    if (type === 'direct' && participantIds.length === 1) {
      return await getOrCreateDM(participantIds[0]);
    }

    // Create conversation
    const { data: conversation, error: convError } = await supabase
      .from('chat_conversations')
      .insert({
        type,
        name: name || null,
        description: description || null,
        project_id: project_id || null,
        created_by: user.id,
      })
      .select()
      .single();

    if (convError) throw convError;

    // Add creator as admin participant
    const participants = [
      { conversation_id: conversation.id, user_id: user.id, role: 'admin' },
    ];

    // Add other participants as members
    participantIds.forEach(uid => {
      if (uid !== user.id) {
        participants.push({
          conversation_id: conversation.id,
          user_id: uid,
          role: 'member',
        });
      }
    });

    const { error: partError } = await supabase
      .from('chat_participants')
      .insert(participants);

    if (partError) throw partError;

    return { data: conversation, error: null };
  } catch (error) {
    console.error('[CHAT] Create conversation error:', error);
    return { data: null, error };
  }
};

/**
 * Get or create a DM with another user
 */
export const getOrCreateDM = async (otherUserId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data: conversationId, error } = await supabase.rpc('get_or_create_dm', {
      p_user1: user.id,
      p_user2: otherUserId,
    });

    if (error) throw error;

    // Fetch the full conversation
    const { data: conversation, error: fetchError } = await supabase
      .from('chat_conversations')
      .select('*')
      .eq('id', conversationId)
      .single();

    if (fetchError) throw fetchError;

    return { data: conversation, error: null };
  } catch (error) {
    console.error('[CHAT] Get or create DM error:', error);
    return { data: null, error };
  }
};

// ============================================================
// MESSAGES
// ============================================================

/**
 * Get paginated messages for a conversation
 */
export const getMessages = async (conversationId, limit = 50, options = {}) => {
  try {
    let query = supabase
      .from('chat_messages')
      .select(`
        *,
        sender:profiles!sender_id(id, full_name, avatar_url, role),
        reply_to:chat_messages!reply_to_id(
          id, content, sender:profiles!sender_id(id, full_name)
        )
      `, { count: 'exact' })
      .eq('conversation_id', conversationId)
      .eq('is_deleted', false);

    if (options.before) {
      query = query.lt('created_at', options.before);
    }

    query = query
      .order('created_at', { ascending: false })
      .limit(limit + 1);

    const { data, count, error } = await query;
    if (error) throw error;

    const hasMore = data?.length > limit;
    const messages = hasMore ? data.slice(0, limit) : data;

    // Normalize reply_to: Supabase returns array for FK joins, we want object or null
    const normalized = (messages || []).map(msg => ({
      ...msg,
      reply_to: Array.isArray(msg.reply_to) ? (msg.reply_to[0] || null) : msg.reply_to,
    }));

    // El bucket es privado: la URL de cada adjunto se firma fresca en cada
    // lectura. Las filas viejas guardaron la URL publica entera; de ahi se
    // recupera el path, asi que no hizo falta migrar datos.
    const withAttachments = await resolveMessagesAttachments(normalized);

    return {
      data: withAttachments.reverse(),
      hasMore,
      totalCount: count,
      error: null,
    };
  } catch (error) {
    console.error('[CHAT] Get messages error:', error);
    return { data: null, hasMore: false, error };
  }
};

/**
 * Send a message
 */
export const sendMessage = async ({ conversation_id, content, message_type = 'text', attachments = [], reply_to_id = null }) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    if (!content?.trim() && attachments.length === 0) {
      throw new Error('El mensaje no puede estar vacio');
    }

    const { data, error } = await supabase
      .from('chat_messages')
      .insert({
        conversation_id,
        sender_id: user.id,
        content: content?.trim() || null,
        message_type,
        // #453: `attachments` is a native JSONB column — write the array
        // as-is. The Supabase client serializes it correctly on its own;
        // calling JSON.stringify() here double-encoded it into a JSON
        // *string* stored inside the jsonb column instead of a native
        // jsonb array, which is why readers (MessageBubble,
        // extractAttachmentUrls below) still defensively JSON.parse() —
        // that defensive parsing stays in place to handle historical rows
        // written before this fix (no data migration performed here).
        //
        // Lo que se guarda es el PATH del objeto, no la URL: el bucket es
        // privado y una URL firmada persistida muere en 1 hora (#332). Ver
        // toPersistedAttachment.
        attachments: attachments.length > 0 ? attachments.map(toPersistedAttachment) : [],
        reply_to_id: reply_to_id || null,
      })
      .select(`
        *,
        sender:profiles!sender_id(id, full_name, avatar_url, role),
        reply_to:chat_messages!reply_to_id(
          id, content, sender:profiles!sender_id(id, full_name)
        )
      `)
      .single();

    if (error) throw error;

    // Normalize reply_to from array to object
    const normalized = {
      ...data,
      reply_to: Array.isArray(data.reply_to) ? (data.reply_to[0] || null) : data.reply_to,
    };

    // MessagesPage empuja esta fila directo al estado (render optimista), y
    // la fila trae el path, no una URL: hay que firmarla o el mensaje recien
    // enviado se ve sin la foto hasta recargar.
    return { data: await resolveMessageAttachments(normalized), error: null };
  } catch (error) {
    console.error('[CHAT] Send message error:', error);
    return { data: null, error };
  }
};

/**
 * Edit a message (own messages only, 15-min window)
 */
export const editMessage = async (messageId, content) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    if (!content?.trim()) {
      throw new Error('El mensaje no puede estar vacio');
    }

    // Verify ownership and time limit
    const { data: existing, error: fetchError } = await supabase
      .from('chat_messages')
      .select('sender_id, created_at')
      .eq('id', messageId)
      .single();

    if (fetchError) throw new Error('Mensaje no encontrado');
    if (existing.sender_id !== user.id) throw new Error('Solo puede editar sus propios mensajes');

    const minutesSinceCreation = (Date.now() - new Date(existing.created_at).getTime()) / 60000;
    if (minutesSinceCreation > 15) {
      throw new Error('Solo puede editar mensajes dentro de los primeros 15 minutos');
    }

    const { data, error } = await supabase
      .from('chat_messages')
      .update({
        content: content.trim(),
        is_edited: true,
        edited_at: new Date().toISOString(),
      })
      .eq('id', messageId)
      .select(`
        *,
        sender:profiles!sender_id(id, full_name, avatar_url, role)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[CHAT] Edit message error:', error);
    return { data: null, error };
  }
};

/**
 * Soft delete a message
 */
export const deleteMessage = async (messageId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data: existing, error: fetchError } = await supabase
      .from('chat_messages')
      .select('sender_id')
      .eq('id', messageId)
      .single();

    if (fetchError) throw new Error('Mensaje no encontrado');
    if (existing.sender_id !== user.id) throw new Error('Solo puede eliminar sus propios mensajes');

    const { error } = await supabase
      .from('chat_messages')
      .update({ is_deleted: true })
      .eq('id', messageId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('[CHAT] Delete message error:', error);
    return { success: false, error };
  }
};

// ============================================================
// READ TRACKING
// ============================================================

/**
 * Mark a conversation as read (update last_read_at)
 */
export const markConversationRead = async (conversationId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { error } = await supabase
      .from('chat_participants')
      .update({ last_read_at: new Date().toISOString() })
      .eq('conversation_id', conversationId)
      .eq('user_id', user.id);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('[CHAT] Mark conversation read error:', error);
    return { success: false, error };
  }
};

/**
 * Get unread counts for all conversations
 */
export const getUnreadCounts = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase.rpc('get_user_unread_counts', {
      p_user_id: user.id,
    });

    if (error) throw error;

    // Convert to map
    const counts = {};
    let total = 0;
    (data || []).forEach(row => {
      counts[row.conversation_id] = row.unread_count;
      total += row.unread_count;
    });

    return { data: counts, total, error: null };
  } catch (error) {
    console.error('[CHAT] Get unread counts error:', error);
    return { data: {}, total: 0, error };
  }
};

// ============================================================
// REALTIME SUBSCRIPTIONS
// ============================================================

/**
 * Subscribe to new AND edited/deleted messages in a conversation (#447).
 *
 * Previously only listened for `INSERT`, so when another participant
 * edited a message or soft-deleted it (`UPDATE chat_messages SET
 * is_deleted = true`), everyone else in the conversation only saw the
 * change after a full page reload. `UPDATE` is now handled the same way as
 * `INSERT` — refetch the full row (with joins) and hand it to the caller —
 * but the callback also receives the Postgres event type
 * (`'INSERT' | 'UPDATE'`) as a second argument so callers can decide
 * whether to append a brand-new message or replace an existing one in
 * place (see MessagesPage.js / ChatView.js).
 */
export const subscribeToMessages = (conversationId, callback) => {
  const emitFullMessage = async (messageId, eventType) => {
    // Fetch complete message with sender info
    const { data, error } = await supabase
      .from('chat_messages')
      .select(`
        *,
        sender:profiles!sender_id(id, full_name, avatar_url, role),
        reply_to:chat_messages!reply_to_id(
          id, content, sender:profiles!sender_id(id, full_name)
        )
      `)
      .eq('id', messageId)
      .single();

    if (!error && data) {
      // Normalize reply_to from array to object
      const normalized = {
        ...data,
        reply_to: Array.isArray(data.reply_to) ? (data.reply_to[0] || null) : data.reply_to,
      };
      // Igual que getMessages: el adjunto llega como path y hay que firmarlo
      // antes de que el componente lo pinte.
      callback(await resolveMessageAttachments(normalized), eventType);
    }
  };

  const channel = supabase
    .channel(`chat-messages:${conversationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `conversation_id=eq.${conversationId}`,
      },
      (payload) => emitFullMessage(payload.new.id, 'INSERT')
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'chat_messages',
        filter: `conversation_id=eq.${conversationId}`,
      },
      (payload) => emitFullMessage(payload.new.id, 'UPDATE')
    )
    .subscribe();

  return {
    unsubscribe: () => supabase.removeChannel(channel),
  };
};

/**
 * Subscribe to conversation-LIST-level updates for a user (#447).
 *
 * Previously only listened for `INSERT` on `chat_messages` (globally,
 * unfiltered), and — more importantly — nothing ever called this from the
 * UI, so the Mensajes sidebar (ordering by `last_message_at`, unread
 * badges for conversations that are NOT the currently-open one) only
 * refreshed via `MessagesPage`'s 30s `refetchInterval` polling. It is now
 * wired up in MessagesPage.js and covers the events that can actually
 * change what the sidebar shows:
 *
 *   - chat_messages INSERT  -> bumps ordering + can add unread. Any
 *     conversation, unfiltered (there is no cheap way to filter
 *     server-side to "conversations this user is in" without a schema
 *     change) — the callback receives the event so the caller can decide
 *     what to invalidate; here it refreshes both `conversations` and
 *     `conversations-unread`.
 *   - chat_messages UPDATE  -> an edit or a soft-delete
 *     (`is_deleted = true`) never changes ordering, but a soft-delete can
 *     reduce the unread count for a conversation the user hasn't opened
 *     yet, so this refreshes `conversations-unread` only.
 *   - chat_participants INSERT filtered to `user_id=eq.${userId}` -> the
 *     user was just added to a new conversation (group/channel/DM), which
 *     the sidebar needs to show without waiting on polling.
 *
 * NOT subscribed: chat_participants DELETE. Realtime `postgres_changes`
 * filters on DELETE match against the table's REPLICA IDENTITY, which for
 * `chat_participants` defaults to primary key only (`id`) — so a
 * `user_id=eq.${userId}` filter can never match a DELETE payload without
 * first running `ALTER TABLE chat_participants REPLICA IDENTITY FULL`
 * (a schema change, deliberately not applied here — see
 * temp/chat_participants_replica_identity.sql for the prepared statement
 * if this is wanted later). Practical impact: being removed from a
 * conversation by someone else still catches up within the existing 30s
 * poll instead of instantly.
 */
export const subscribeToConversations = (userId, callback) => {
  const channel = supabase
    .channel(`chat-updates:${userId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'chat_messages' },
      () => callback({ table: 'chat_messages', eventType: 'INSERT' })
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'chat_messages' },
      () => callback({ table: 'chat_messages', eventType: 'UPDATE' })
    )
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_participants',
        filter: `user_id=eq.${userId}`,
      },
      () => callback({ table: 'chat_participants', eventType: 'INSERT' })
    )
    .subscribe();

  return {
    unsubscribe: () => supabase.removeChannel(channel),
  };
};

// ============================================================
// PARTICIPANTS
// ============================================================

/**
 * Add participants to a conversation
 */
export const addParticipants = async (conversationId, userIds) => {
  try {
    const participants = userIds.map(uid => ({
      conversation_id: conversationId,
      user_id: uid,
      role: 'member',
    }));

    const { data, error } = await supabase
      .from('chat_participants')
      .insert(participants)
      .select();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[CHAT] Add participants error:', error);
    return { data: null, error };
  }
};

/**
 * Remove a participant from a conversation
 */
export const removeParticipant = async (conversationId, userId) => {
  try {
    const { error } = await supabase
      .from('chat_participants')
      .delete()
      .eq('conversation_id', conversationId)
      .eq('user_id', userId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('[CHAT] Remove participant error:', error);
    return { success: false, error };
  }
};

/**
 * Get participants of a conversation
 *
 * Includes `user_id` and `last_read_at` (in addition to the joined profile)
 * so callers can derive read-receipt state per message — see #446: there is
 * no per-message `is_read` column, read tracking lives on
 * `chat_participants.last_read_at`.
 */
export const getParticipants = async (conversationId) => {
  try {
    const { data, error } = await supabase
      .from('chat_participants')
      .select(`
        id, user_id, role, joined_at, is_muted, last_read_at,
        user:profiles!user_id(id, full_name, avatar_url, role)
      `)
      .eq('conversation_id', conversationId);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('[CHAT] Get participants error:', error);
    return { data: null, error };
  }
};

// ============================================================
// BITACORA INTEGRATION
// ============================================================

/**
 * Link a conversation to the project bitacora/timeline
 *
 * `attachment_urls` (when the source chat message carries photo evidence)
 * is copied onto the `chat_bitacora_links` row so the photo survives as
 * durable bitacora evidence even if the original chat message is later
 * edited or soft-deleted. A text `summary` is required UNLESS at least one
 * attachment is provided, in which case a sensible default is used — an
 * image-only message ("just the photo") is valid evidence on its own.
 *
 * See issue #267.
 */
export const linkToBitacora = async ({
  conversation_id,
  project_id,
  summary,
  from_message_id,
  to_message_id,
  attachment_urls = [],
}) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const trimmedSummary = summary?.trim();
    const hasAttachments = Array.isArray(attachment_urls) && attachment_urls.length > 0;

    if (!trimmedSummary && !hasAttachments) {
      throw new Error('El resumen es requerido');
    }

    const finalSummary = trimmedSummary || 'Foto compartida desde el chat';

    // Create project_history entry
    const { data: historyEntry, error: historyError } = await supabase
      .from('project_history')
      .insert({
        project_id,
        changed_by: user.id,
        change_type: 'chat_linked',
        new_value: finalSummary,
        notes: finalSummary,
      })
      .select('id')
      .single();

    if (historyError) throw historyError;

    // Create bitacora link
    const { data, error } = await supabase
      .from('chat_bitacora_links')
      .insert({
        conversation_id,
        project_id,
        summary: finalSummary,
        linked_by: user.id,
        from_message_id: from_message_id || null,
        to_message_id: to_message_id || null,
        history_entry_id: historyEntry.id,
        attachment_urls: hasAttachments ? attachment_urls : null,
      })
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[CHAT] Link to bitacora error:', error);
    return { data: null, error };
  }
};

// ============================================================
// USERS & SEARCH
// ============================================================

/**
 * Get available users for creating conversations
 */
export const getAvailableUsers = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url, role')
      .neq('id', user.id)
      .order('full_name');

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('[CHAT] Get available users error:', error);
    return { data: null, error };
  }
};

/**
 * Search messages across conversations
 *
 * `term` matches message content (ILIKE). `options.dateFrom` / `options.dateTo`
 * (ISO strings) additionally scope results to a `created_at` range — used by
 * the Mensajes internal search bar (#427) to support date-only searches (no
 * text term) as well as text+date combined. A term is still required unless
 * a date range is supplied, so a stray empty call can't dump the whole table.
 */
export const searchMessages = async (term, options = {}) => {
  try {
    const trimmedTerm = (term || '').trim();
    const hasDateFilter = !!(options.dateFrom || options.dateTo);

    if (trimmedTerm.length < 2 && !hasDateFilter) {
      throw new Error('El termino de busqueda debe tener al menos 2 caracteres');
    }

    let query = supabase
      .from('chat_messages')
      .select(`
        *,
        sender:profiles!sender_id(id, full_name, avatar_url),
        conversation:chat_conversations!conversation_id(id, type, name, project_id)
      `)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .limit(options.limit || 20);

    if (trimmedTerm.length >= 2) {
      // #739/#742 — saneado SÍ, normalización NO. `chat_messages` es la única
    // tabla del barrido sin techo estructural (crece con el uso), así que quedó
    // fuera del conjunto normalizado por decisión declarada en #742. Con los dos
    // lados crudos la conducta es la de siempre —sensible a tildes— pero
    // CONSISTENTE; normalizar sólo el término la dejaría peor.
    query = query.ilike('content', `%${terminoParaFiltroSinNormalizar(trimmedTerm)}%`);
    }

    if (options.conversationId) {
      query = query.eq('conversation_id', options.conversationId);
    }

    if (options.dateFrom) {
      query = query.gte('created_at', options.dateFrom);
    }

    if (options.dateTo) {
      query = query.lte('created_at', options.dateTo);
    }

    const { data, error } = await query;
    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[CHAT] Search messages error:', error);
    return { data: null, error };
  }
};

/**
 * Get a single conversation by ID with full details
 */
export const getConversation = async (conversationId) => {
  try {
    const { data, error } = await supabase
      .from('chat_conversations')
      .select(`
        *,
        project:projects(id, title, project_number)
      `)
      .eq('id', conversationId)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[CHAT] Get conversation error:', error);
    return { data: null, error };
  }
};

// ============================================================
// CONVERSATION MANAGEMENT (archive, delete, leave)
// ============================================================

/**
 * Archive/unarchive a conversation (admin/creator only)
 */
export const archiveConversation = async (conversationId, archive = true) => {
  try {
    const { error } = await supabase
      .from('chat_conversations')
      .update({ is_archived: archive })
      .eq('id', conversationId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('[CHAT] Archive conversation error:', error);
    return { success: false, error };
  }
};

/**
 * Delete a conversation (removes all participants, messages stay for audit)
 * Only the creator or admin can delete.
 */
export const deleteConversation = async (conversationId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Verify the user is creator or admin participant
    const { data: conv } = await supabase
      .from('chat_conversations')
      .select('created_by')
      .eq('id', conversationId)
      .single();

    const { data: participant } = await supabase
      .from('chat_participants')
      .select('role')
      .eq('conversation_id', conversationId)
      .eq('user_id', user.id)
      .single();

    const isCreator = conv?.created_by === user.id;
    const isAdmin = participant?.role === 'admin';

    if (!isCreator && !isAdmin) {
      throw new Error('Solo el creador o admin puede eliminar la conversación');
    }

    // Remove all participants (cascade will handle, but explicit is safer)
    await supabase
      .from('chat_participants')
      .delete()
      .eq('conversation_id', conversationId);

    // Archive the conversation (soft delete — keep messages for audit)
    await supabase
      .from('chat_conversations')
      .update({ is_archived: true })
      .eq('id', conversationId);

    return { success: true, error: null };
  } catch (error) {
    console.error('[CHAT] Delete conversation error:', error);
    return { success: false, error };
  }
};

/**
 * Mute/unmute a conversation
 */
export const muteConversation = async (conversationId, mute = true) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { error } = await supabase
      .from('chat_participants')
      .update({ is_muted: mute })
      .eq('conversation_id', conversationId)
      .eq('user_id', user.id);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('[CHAT] Mute conversation error:', error);
    return { success: false, error };
  }
};

/**
 * Leave a conversation (remove self as participant)
 */
export const leaveConversation = async (conversationId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { error } = await supabase
      .from('chat_participants')
      .delete()
      .eq('conversation_id', conversationId)
      .eq('user_id', user.id);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('[CHAT] Leave conversation error:', error);
    return { success: false, error };
  }
};

// ============================================================
// COMPAT ALIASES — keep old import names working after refactor
// ============================================================

/** @deprecated Use getConversations */
export const listConversations = getConversations;

/** @deprecated Use subscribeToMessages */
export const subscribeToConversation = (conversationId, callback) =>
  subscribeToMessages(conversationId, callback);

/**
 * @deprecated Use addParticipants (plural)
 * Wraps addParticipants to accept a single userId
 */
export const addParticipant = (conversationId, userId) =>
  addParticipants(conversationId, [userId]);

/**
 * Extract plain attachment URLs from a chat_messages row's `attachments`
 * JSONB column. Handles both the intended shape (array of
 * `{url, type, name}` objects / plain strings) and the legacy
 * double-encoded string shape some HISTORICAL rows still have — `sendMessage`
 * used to write `JSON.stringify(attachments)` into this JSONB column
 * (fixed in #453), but rows written before that fix are still strings, and
 * no data migration was run to fix them in place — see MessageBubble's
 * identical defensive parsing.
 */
const extractAttachmentUrls = (message) => {
  return parseAttachments(message?.attachments)
    // El mensaje que llega aca viene del render, o sea con la URL FIRMADA ya
    // resuelta. Persistir eso en chat_bitacora_links seria evidencia muerta a
    // la hora (#332), asi que se guarda el PATH; la bitacora lo firma al leer
    // (timelineService). Un adjunto externo, que no tiene path, se guarda tal
    // cual.
    .map((item) => chatAttachmentPath(item) || (typeof item === 'string' ? item : item?.url))
    .filter(Boolean);
};

/** @deprecated Use linkToBitacora */
export const shareMessageToBitacora = (message, { projectId } = {}) =>
  linkToBitacora({
    conversation_id: message.conversation_id,
    project_id: projectId,
    summary: message.content,
    from_message_id: message.id,
    to_message_id: message.id,
    attachment_urls: extractAttachmentUrls(message),
  });

/**
 * Conversation type constants (mirrors DB enum values)
 *
 * NOTE: The DB check constraint on chat_conversations.type is
 * ('channel', 'direct', 'group') — a project-scoped conversation is
 * stored as 'channel', not 'project'. See migration
 * 20260214180000_chat_system.sql.
 */
export const CONVERSATION_TYPE = {
  DM: 'direct',
  GROUP: 'group',
  PROJECT: 'channel',
};

const chatService = {
  uploadChatAttachment,
  chatAttachmentPath,
  resolveChatAttachmentUrl,
  resolveMessageAttachments,
  resolveMessagesAttachments,
  CHAT_ATTACHMENTS_BUCKET,
  getConversations,
  createConversation,
  getOrCreateDM,
  getMessages,
  sendMessage,
  editMessage,
  deleteMessage,
  markConversationRead,
  getUnreadCounts,
  subscribeToMessages,
  subscribeToConversations,
  addParticipants,
  removeParticipant,
  getParticipants,
  linkToBitacora,
  getAvailableUsers,
  searchMessages,
  getConversation,
  archiveConversation,
  deleteConversation,
  leaveConversation,
  muteConversation,
  // compat aliases
  listConversations,
  subscribeToConversation,
  addParticipant,
  shareMessageToBitacora,
  CONVERSATION_TYPE,
};

export default chatService;
